import {
  featureStrategyEvaluators,
  type FeatureEvaluationContext,
  type FeatureFlagEnvironment,
  type RolloutStrategyType,
  type FeatureTargetRule,
} from '@/feature-flags/FeatureStrategies';

export interface FeatureFlag {
  id: string;
  key: string;
  name: string;
  description: string;
  enabled: boolean;
  status: 'enabled' | 'disabled' | 'locked' | 'scheduled';
  productId: string | null;
  environment: FeatureFlagEnvironment;
  rolloutPercentage: number;
  strategy: RolloutStrategyType;
  owner: string;
  createdDate: string;
  lastModified: string;
  modifiedBy: string;
  tags: string[];
  targetRules: FeatureTargetRule[];
}

export interface FeatureEvaluationResult {
  key: string;
  enabled: boolean;
  reason:
    | 'not-found'
    | 'disabled'
    | 'locked'
    | 'environment-mismatch'
    | 'product-mismatch'
    | 'strategy-rejected'
    | 'enabled';
  strategy: RolloutStrategyType | null;
}

export function evaluateFeatureFlag(
  featureFlag: FeatureFlag | null,
  context: FeatureEvaluationContext,
): FeatureEvaluationResult {
  if (!featureFlag) {
    return {
      key: '',
      enabled: false,
      reason: 'not-found',
      strategy: null,
    };
  }

  if (featureFlag.status === 'locked') {
    return {
      key: featureFlag.key,
      enabled: false,
      reason: 'locked',
      strategy: featureFlag.strategy,
    };
  }

  if (!featureFlag.enabled || featureFlag.status === 'disabled') {
    return {
      key: featureFlag.key,
      enabled: false,
      reason: 'disabled',
      strategy: featureFlag.strategy,
    };
  }

  if (context.environment && context.environment !== featureFlag.environment) {
    return {
      key: featureFlag.key,
      enabled: false,
      reason: 'environment-mismatch',
      strategy: featureFlag.strategy,
    };
  }

  if (featureFlag.productId && context.productId) {
    if (featureFlag.productId !== context.productId) {
      return {
        key: featureFlag.key,
        enabled: false,
        reason: 'product-mismatch',
        strategy: featureFlag.strategy,
      };
    }
  }

  const evaluator = featureStrategyEvaluators[featureFlag.strategy];

  const accepted = evaluator({
    enabled: featureFlag.enabled,
    rolloutPercentage: featureFlag.rolloutPercentage,
    targetRules: featureFlag.targetRules,
    context,
  });

  if (!accepted) {
    return {
      key: featureFlag.key,
      enabled: false,
      reason: 'strategy-rejected',
      strategy: featureFlag.strategy,
    };
  }

  return {
    key: featureFlag.key,
    enabled: true,
    reason: 'enabled',
    strategy: featureFlag.strategy,
  };
}
